import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ApiDataFields } from '@/Domain/components/Action/ApiDataTable/ApiDataFields';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { FormField, FormItem } from '@/Domain/components/Shared/Form/Form';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';

export enum HttpItemCategory {
  HEADERS = 'headers',
  BODY_ITEMS = 'body_items',
  QUERY_ITEMS = 'query_items',
}

export type JsonSwitchProps = {
  formName: string;
  isActive: boolean;
};

export enum ApiDataFieldKey {
  VALUE = 'value',
  CONTENT = 'content',
}

type Props = {
  name: HttpItemCategory;
  form: UseFormReturn<any>;
  dataFieldKey?: ApiDataFieldKey;
  withInsertBadge: boolean;
  jsonSwitchParams?: JsonSwitchProps;
};

export const ApiDataTable = ({
  name,
  form,
  dataFieldKey = ApiDataFieldKey.VALUE,
  withInsertBadge,
  jsonSwitchParams,
}: Props) => {
  const { t } = useTranslation('actions_page');

  const { fields, append, remove } = useFieldArray({ control: form.control, name });

  const handleAddEmptyField = () => {
    if (dataFieldKey === 'value') {
      return append({ id: uuidv4(), name: '', value: '' });
    }

    return append({ id: uuidv4(), name: '', content: '' });
  };

  const handleRemoveField = (index: number) => remove(index);

  return (
    <TooltipProvider>
      <div>
        <section className="relative grid h-16 grid-cols-[1fr_1fr_auto_34px] items-center border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
          {!jsonSwitchParams?.isActive ? (
            <span>{t('sheet.form.api_endpoint.api_data.table.key')}</span>
          ) : null}
          {!jsonSwitchParams?.isActive ? (
            <span>{t('sheet.form.api_endpoint.api_data.table.value')}</span>
          ) : null}
          <span />
          <div className="flex items-center gap-2 absolute right-20">
            {!!jsonSwitchParams && (
              <FormField
                name={jsonSwitchParams.formName}
                render={({ field }) => (
                  <FormItem className="flex gap-2 items-center">
                    <Switch
                      size="sm"
                      checked={field.value}
                      onCheckedChange={(check) => field.onChange(check)}
                    />
                    <span className="text-xs font-medium text-muted-foreground">JSON</span>
                  </FormItem>
                )}
              />
            )}
          </div>
          {!jsonSwitchParams?.isActive && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  size="sm"
                  data-testid="api-data-add-button"
                  onClick={handleAddEmptyField}
                >
                  <AddIcon />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>{t('tooltip.add')}</TooltipContent>
            </Tooltip>
          )}
        </section>

        <ApiDataFields
          name={name}
          fields={fields}
          jsonSwitchParams={jsonSwitchParams}
          dataFieldKey={dataFieldKey}
          withInsertBadge={withInsertBadge}
          onAdd={handleAddEmptyField}
          onRemove={handleRemoveField}
        />
      </div>
    </TooltipProvider>
  );
};
